import type { StyleBindings } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';

export interface StyleClassDeclaration {
  id: string;
  title: string;
  overwriteDisabled?: boolean;
}

export enum ComponentName {
  Card = 'custom-feefe5ed-2f21-4e89-8d10-14f81a1ee7a7',
  Icon = 'custom-77e3ebf8-aacf-451f-a927-925e390b47a7',
  Title = 'custom-7331eb73-e479-4418-b86a-d18e746d0b09',
  Paragraph = 'custom-ad1c810a-2db9-45a8-af24-6fbd192fd753',
  Image = 'custom-230ca122-bfdb-4db0-b288-77772ff7cb5e',
  Spinner = 'custom-75d00364-b5cd-460c-8d10-a505f2f0a387',
  DropdownField = 'custom-6078fe78-08e9-42ca-a34e-d3d2f1769902',
  CheckboxField = 'custom-4dfd7ed9-5c70-4706-a3e3-a622869574ba',
  SliderField = 'custom-3253732e-edb1-4de2-b0cb-edea15a02eaf',
  PasswordField = 'custom-82ce179a-058e-475e-9779-d4a269cf35c1',
  InputField = 'custom-45c5b583-8fef-4209-a2ca-b79f98581a10',
  Button = 'custom-2ee5cce2-9279-450a-bc46-2c0e43224be1',
  Row = 'layout',
  ScrollView = 'custom-8a78aa43-d249-43a9-95bd-c15d2d7d584e',
  Container = 'vertical_box_layout',
  RecyclerView = 'custom-a73b1b9b-b017-47ab-89d1-bea1e3790efe',
  ImageListItem = 'custom-da27bed9-1bf8-41aa-a999-858fa7ad1bd0',
  LargeImageListItem = 'custom-a482ad57-c6a9-45e9-b768-25905b4305ed',
  ListItem = 'custom-93627f49-4a20-45ec-8141-f573f9ba3151',
  IconListItem = 'custom-20141fbb-d231-421e-b9b7-dd332ffd1ff7',
  SearchBar = 'custom-6714d444-16c0-4e7d-a204-b4db8955aad4',
  ToggleListItem = 'custom-a87f5fab-1045-469f-8517-936c2389f0c7',
  ListSectionHeader = 'custom-0df79aeb-50f0-4271-b55c-50f2fcea5f47',
  ListDivider = 'custom-0336f63d-f5c6-4cfe-8951-e0e9a5afe186',
  Checkbox = 'custom-e6659a2e-ad70-44ba-9665-5e17181f88ea',
  Dropdown = 'custom-dc53f48c-d59d-4be8-94e5-16421c94f6fd',
  Input = 'custom-b304608e-6fe0-463b-b879-e58a1ac2d086',
  Slider = 'custom-11aa897c-cb79-4072-aba1-5febed339e12',
  Toggle = 'custom-26ea632b-6a4a-4da0-8806-c8c58b6d4a58',
  AreaChart = 'custom-1f0f0d2c-4ff1-4d2f-8677-101c8e830c0e',
  BarChart = 'custom-e0fdc495-8fbe-4519-baea-2a076f68be5f',
  LineChart = 'custom-3443a8a8-6d16-4f1b-9f4d-ddca5a1f892c',
  PieChart = 'custom-85e25b86-0078-4ed1-93d4-e44b95b954e6',
  ListItemContainer = 'custom-2a9f47c7-d494-48ce-97be-b094ae7167cd',
  CardContainer = 'custom-053bf9f5-c2ec-45b1-8839-62020f8e59bd',
}

export const designSystemStyleClassesByComponentName: Record<string, StyleClassDeclaration[] | undefined> = {
  [ComponentName.Icon]: [
    {
      id: 'primary',
      title: 'Primary icon',
    },
    {
      id: 'secondary',
      title: 'Secondary icon',
    },
    {
      id: 'tertiary',
      title: 'Tertiary icon',
    },
  ],
  [ComponentName.Title]: [
    {
      id: 'contentPrimary',
      title: 'Content Primary Heading',
    },
    {
      id: 'contentSecondary',
      title: 'Content Secondary Heading',
    },
    {
      id: 'contentTertiary',
      title: 'Content Tertiary Heading',
    },
    {
      id: 'pagePrimary',
      title: 'Page Primary Heading',
    },
    {
      id: 'pageSecondary',
      title: 'Page Secondary Heading',
    },
    {
      id: 'pageMaster',
      title: 'Page Master Heading',
    },
  ],
  [ComponentName.Paragraph]: [
    {
      id: 'primaryParagraph',
      title: 'Primary Paragraph',
    },
    {
      id: 'secondaryParagraph',
      title: 'Secondary Paragraph',
    },
    {
      id: 'leadParagraph',
      title: 'Lead Paragraph',
    },
    {
      id: 'primaryLabel',
      title: 'Primary Label',
    },
    {
      id: 'secondaryLabel',
      title: 'Secondary Label',
    },
    {
      id: 'tertiaryLabel',
      title: 'Tertiary Label',
    },
  ],
  [ComponentName.Image]: [
    {
      id: 'default',
      title: 'Image',
    },
  ],
  [ComponentName.Spinner]: [
    {
      id: 'default',
      title: 'Default spinner',
    },
  ],
  [ComponentName.DropdownField]: [
    {
      id: 'default',
      title: 'Default Dropdown',
    },
  ],
  [ComponentName.CheckboxField]: [
    {
      id: 'default',
      title: 'Default Checkbox',
    },
  ],
  [ComponentName.SliderField]: [
    {
      id: 'default',
      title: 'Default Slider',
    },
  ],
  [ComponentName.PasswordField]: [
    {
      id: 'default',
      title: 'Default Password Field',
    },
  ],
  [ComponentName.InputField]: [
    {
      id: 'default',
      title: 'Default Input field',
    },
  ],
  [ComponentName.Button]: [
    {
      id: 'primary',
      title: 'Primary Button',
    },
    {
      id: 'secondary',
      title: 'Secondary Button',
    },
    {
      id: 'tertiary',
      title: 'Tertiary Button',
    },
    {
      id: 'danger',
      title: 'Danger Button',
    },
    {
      id: 'success',
      title: 'Success Button',
    },
  ],
  [ComponentName.Container]: [
    {
      id: 'layout',
      title: 'Layout Container',
      overwriteDisabled: true,
    },
    {
      id: 'primaryCard',
      title: 'Primary Card',
    },
    {
      id: 'secondaryCard',
      title: 'Secondary Card',
    },
    {
      id: 'formField',
      title: 'Form Field',
    },
    {
      id: 'listItem',
      title: 'List Item',
    },
    {
      id: 'listHeader',
      title: 'List Header',
    },
  ],
  [ComponentName.ListItemContainer]: [
    {
      id: 'listItem',
      title: 'List Item',
    },
  ],
  [ComponentName.CardContainer]: [
    {
      id: 'primaryCard',
      title: 'Primary Card',
    },
  ],
  [ComponentName.Dropdown]: [
    {
      id: 'default',
      title: 'Default Dropdown',
    },
  ],
  [ComponentName.Input]: [
    {
      id: 'default',
      title: 'Default Input',
    },
  ],
  [ComponentName.Slider]: [
    {
      id: 'default',
      title: 'Default Slider',
    },
  ],
  [ComponentName.Toggle]: [
    {
      id: 'default',
      title: 'Default Toggle',
    },
  ],
  [ComponentName.Checkbox]: [
    {
      id: 'default',
      title: 'Default Checkbox',
    },
  ],
  [ComponentName.ListItem]: [
    {
      id: 'default',
      title: 'Default List Item',
    },
  ],
  [ComponentName.ImageListItem]: [
    {
      id: 'default',
      title: 'Default Image List Item',
    },
  ],
  [ComponentName.LargeImageListItem]: [
    {
      id: 'default',
      title: 'Default Large Image List Item',
    },
  ],
  [ComponentName.IconListItem]: [
    {
      id: 'default',
      title: 'Default Icon List Item',
    },
  ],
  [ComponentName.ToggleListItem]: [
    {
      id: 'default',
      title: 'Default Toggle List Item',
    },
  ],
  [ComponentName.ListSectionHeader]: [
    {
      id: 'default',
      title: 'Default List Section Header',
    },
  ],
  [ComponentName.ListDivider]: [
    {
      id: 'default',
      title: 'Default List Divider',
    },
  ],
  [ComponentName.Card]: [
    {
      id: 'default',
      title: 'Default Card',
    },
  ],
  [ComponentName.SearchBar]: [
    {
      id: 'default',
      title: 'Default Search Bar',
    },
  ],
  [ComponentName.Row]: [
    {
      id: 'default',
      title: 'Default Row',
    },
  ],
  [ComponentName.ScrollView]: [
    {
      id: 'default',
      title: 'Scroll view',
      overwriteDisabled: true,
    },
  ],
  [ComponentName.RecyclerView]: [
    {
      id: 'default',
      title: 'Recycler view',
      overwriteDisabled: true,
    },
  ],
  [ComponentName.AreaChart]: [
    {
      id: 'default',
      title: 'Chart',
    },
  ],
  [ComponentName.BarChart]: [
    {
      id: 'default',
      title: 'Chart',
    },
  ],
  [ComponentName.LineChart]: [
    {
      id: 'default',
      title: 'Chart',
    },
  ],
  [ComponentName.PieChart]: [
    {
      id: 'default',
      title: 'Chart',
    },
  ],
};

export const designSystemDefaultCompositeComponentLayoutProperties: StyleBindings = {
  dimensionsBehavior: 'expandWidth',
  gapEnabled: true,
  gapAfter: {
    source: 'theme',
    key: '$componentSpacing_M',
  },
};
